/**
 * useConsentGate — the one interception every generating CTA goes through (plan
 * 2026_09_01_tos_gate.md §8.2/§8.6 step 1). Wraps `gate.ts`'s pure reducer with the browser flag
 * (`storage.ts`) and the pending press: `press(fire)` either runs `fire` now, stashes it and opens
 * the card, nudges the checkbox, or writes the flag and runs the winning closure (`resolveFire`
 * below).
 *
 * Staleness (2026-09-01 fix round, item 1): `press` reads the CURRENT machine state and browser
 * flag off `stateRef`/`browserAcceptedRef`, not off the `state`/`browserAccepted` variables closed
 * over at whichever render created this particular `press` instance. Both refs are kept live —
 * `stateRef` by a plain assignment on every render (synchronous, before any effect can run),
 * `browserAcceptedRef` by a direct write inside the mount effect below (not only through
 * `setBrowserAccepted`, which only reaches a FUTURE render). This is what makes a `press` invoked
 * from a stale closure — `StageComponent.tsx`'s sign-in-return effect is the concrete case: a `[]`
 * effect that captures this hook's very first render's `gate`, and therefore its very first
 * `press`, before this hook's own mount effect has necessarily been OBSERVED by a re-render — still
 * judge against the truth as of the moment it actually runs. React guarantees a custom hook's own
 * effects run before effects declared later in the same calling component (registration order is
 * call order, on one fiber), so by the time such a caller effect fires, this hook's mount effect —
 * registered earlier, because `useConsentGate()` is called before that caller effect — has already
 * written both refs.
 *
 * The stash (`pendingFireRef`) is the FIRST press only (the `idle -> asked` transition) — a press
 * while already `asked` (`focus_checkbox`) doesn't touch it. `resolveFire` (2026-09-01 fix round,
 * item 2) decides which closure the accepting press actually runs: the fresh `fire` THIS call was
 * handed, when there is one, and the stash only when this call carries none. A generating surface
 * whose direction/target can change between the ask and the tick — the wall's bar verb, typing a
 * direction after the card opens — must fire what the accepting press itself carries, never the
 * words the FIRST press happened to close over; narcis's file-drop CTA is the opposite case (a
 * drop's `FileList` cannot be reproduced by a plain "Continue" click, so that click passes no fire
 * of its own and the stash is the only thing left to run). `resolveFire` is exported and exercised
 * directly by `__tests__/useConsentGate.spec.ts` — this project runs `testEnvironment: 'node'` with
 * no jsdom/`@testing-library/react` installed (see `core/hooks/__tests__/useModelIntake.spec.ts`'s
 * own header) and this worktree cannot `npm install` to add either, so the hook's own React wiring
 * (the refs above, the effect ordering, the double-press latch below) is exercised by the dev-verify
 * loop; the decision logic that changed is pure and gets real, executable coverage instead.
 *
 * Folding (Escape / the surface closing the card) drops the stash too: without this, arming a
 * second target after folding an unresolved first ask would let a later tick+press resurrect the
 * FIRST target's stale closure instead of (or alongside) the second.
 *
 * The accepting branch is latched by `acceptingRef`, a synchronous guard distinct from the machine's
 * own `accepted` status: `dispatch({type: 'ACCEPTED'})` only takes effect on a LATER render, so two
 * presses landing in the same tick (a double-click, Enter racing a click) would otherwise both read
 * `status === 'ticked'` and both run `accept_then_fire` — writing the flag twice is harmless, but
 * firing the pending action twice, and tracking `consent_accepted` twice, is not. The latch is set
 * once and never reset: once true, `pressOutcome` itself returns `'fire'` for every later press
 * anyway (`accepted` is absorbing).
 *
 * `PackHeroStage.tsx`'s own auto-fire effect (`gate.press(generate)`) lists `gate` in its dependency
 * array and therefore re-runs on every render of that component, since this hook returns a fresh
 * object each render — it is not memoized here, and does not need to be: its own two ref guards
 * (`pendingAutoFireRef`, `didAutoFireRef`) make every re-run past the first real fire a no-op, so it
 * is left as it is rather than reached into for a "latest ref" wrapper it doesn't need.
 */
import {useEffect, useReducer, useRef, useState} from 'react'
import {track} from '@core/utils/analytics'
import type {ConsentSurface} from '@core/utils/analytics'
import {gateReducer, pressOutcome, type GateState, type GateStatus, type PressOutcome} from './gate'
import {readAccepted, writeAccepted, type ConsentHouse} from './storage'

export type {ConsentHouse, ConsentSurface}

/** The ToS gate: acceptance is remembered per house in the browser and counted in analytics. */
export interface RememberedGateOptions {
    house: ConsentHouse
    surface: ConsentSurface
}

/** A per-press consent that is not the ToS (the model intake's photo-use consent, asked on every
 *  Start): the same two-press machine, but nothing is read from or written to the browser and no
 *  `consent_*` event is sent, so it can never count as, or stand in for, ToS acceptance. */
export interface ForgetfulGateOptions {
    remember: false
}

export type UseConsentGateOptions = RememberedGateOptions | ForgetfulGateOptions

export interface ConsentCardProps {
    checked: boolean
    onTick: (checked: boolean) => void
    autoFocus: boolean
    /** Bumped on every 'ask' and 'focus_checkbox' outcome — the card keys its focus effect off this
     *  instead of running once on mount, so a press that lands while the card is already open and
     *  unticked moves focus back to the checkbox instead of doing nothing (2026-09-01 fix round,
     *  item 3). */
    focusToken: number
}

export interface UseConsentGateResult {
    status: GateStatus
    checked: boolean
    open: boolean
    accepted: boolean
    /** The single interception every generating CTA goes through. `fire` is optional: omitting it
     *  means "resume whatever is already stashed, if anything" — see `resolveFire` below. */
    press: <T>(fire?: () => T) => void
    tick: (checked: boolean) => void
    fold: () => void
    cardProps: ConsentCardProps
}

const INITIAL_STATE: GateState = {status: 'idle', hasPending: false}

/** Which closure an accepting press actually runs: the fresh one this call was handed, when there
 *  is one, and the stashed one only when this call carries none. Pure — no refs, no dispatch — so
 *  it is real, executable coverage for the rule `useConsentGate`'s header doc states (item 2). */
export function resolveFire<T>(fresh: (() => T) | undefined, stashed: (() => T) | null): (() => T) | null {
    return fresh ?? stashed
}

/** Whether a press's outcome should move focus to the card's checkbox — both the outcome that
 *  first opens the card ('ask') and the outcome that nudges it while it is already open and still
 *  unticked ('focus_checkbox'). Pure, same reasoning as `resolveFire` above: item 3's rule gets
 *  real, executable coverage instead of living only in the branching below. */
export function shouldNudgeFocus(outcome: PressOutcome): boolean {
    return outcome === 'ask' || outcome === 'focus_checkbox'
}

export function useConsentGate(options: UseConsentGateOptions): UseConsentGateResult {
    const scope = 'remember' in options ? null : options
    const [state, dispatch] = useReducer(gateReducer, INITIAL_STATE)
    const [folded, setFolded] = useState(false)
    const [browserAccepted, setBrowserAccepted] = useState(false)
    const [focusToken, setFocusToken] = useState(0)
    const pendingFireRef = useRef<(() => unknown) | null>(null)
    const acceptingRef = useRef(false)

    // Kept current on every render — a plain assignment, not an effect, so it is correct by the
    // time ANY effect for this commit runs, including one registered on this same component before
    // React has even started running effects. See the header doc on `press`'s staleness fix.
    const stateRef = useRef(state)
    stateRef.current = state
    const browserAcceptedRef = useRef(browserAccepted)
    browserAcceptedRef.current = browserAccepted

    // localStorage is unreadable during SSR — read once, after mount, exactly like the
    // `useToSAccepted` hooks this replaces.
    useEffect(() => {
        if (!scope) return
        const flag = readAccepted(scope.house)
        // Written straight to the ref (not only through `setBrowserAccepted`, which only reaches a
        // future render) so a `press` called from an effect that runs immediately after this one —
        // even one on another fiber — reads the flag the instant it is known, not once React gets
        // around to re-rendering this component.
        browserAcceptedRef.current = flag
        setBrowserAccepted(flag)
        // eslint-disable-next-line react-hooks/exhaustive-deps -- read once per mount; `house` is
        // fixed for the life of a render site.
    }, [])

    const accepted = browserAccepted || state.status === 'accepted'
    const checked = state.status === 'ticked' || state.status === 'accepted'
    const open = (state.status === 'asked' || state.status === 'ticked') && !folded

    function press<T>(fire?: () => T): void {
        const currentState = stateRef.current
        const currentAccepted = browserAcceptedRef.current || currentState.status === 'accepted'
        const outcome = pressOutcome(currentState, currentAccepted)

        if (outcome === 'fire') {
            fire?.()
            return
        }
        if (outcome === 'ask') {
            pendingFireRef.current = fire ?? null
            setFolded(false)
            dispatch({type: 'PRESS', accepted: currentAccepted})
            if (shouldNudgeFocus(outcome)) setFocusToken((t) => t + 1)
            if (scope) track('consent_shown', scope)
            return
        }
        if (outcome === 'focus_checkbox') {
            setFolded(false)
            if (shouldNudgeFocus(outcome)) setFocusToken((t) => t + 1)
            return
        }
        // accept_then_fire — the second press is the consent gesture. Latched against a second,
        // synchronous press landing before React applies the ACCEPTED dispatch (see the header doc).
        if (acceptingRef.current) return
        acceptingRef.current = true
        if (scope) writeAccepted(scope.house)
        browserAcceptedRef.current = true
        setBrowserAccepted(true)
        dispatch({type: 'ACCEPTED'})
        if (scope) track('consent_accepted', scope)
        const pending = pendingFireRef.current
        pendingFireRef.current = null
        // The stash is necessarily type-erased (`press<T>` is called with a different T at every
        // call site sharing this one ref) — the cast is safe because nothing downstream inspects
        // the winning closure's return value, only calls it.
        resolveFire(fire, pending as (() => T) | null)?.()
    }

    function tick(checked: boolean): void {
        dispatch({type: 'TICK', checked})
    }

    function fold(): void {
        setFolded(true)
        // Drops whatever was stashed: arming a different target after folding an unresolved ask
        // must not let a later tick+press resurrect the FIRST target's stale closure.
        pendingFireRef.current = null
        dispatch({type: 'FOLD'})
    }

    return {
        status: state.status,
        checked,
        open,
        accepted,
        press,
        tick,
        fold,
        cardProps: {checked, onTick: tick, autoFocus: true, focusToken},
    }
}
